import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  useTheme,
  Fade,
  Slide,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  SportsCricket,
  TrendingUp,
  Security,
  PersonOff,
  CheckCircle,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useEffect } from 'react';
import { userService } from '../../services/apiServices';

const validationSchema = yup.object({
  userName: yup
    .string('Enter your username')
    .required('Username is required'),
  password: yup
    .string('Enter your password')
    .min(6, 'Password should be of minimum 6 characters length')
    .required('Password is required'),
});

const Login = () => {
  const theme = useTheme();
  const { login, isAuthenticated, loading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [deactivatedDialogOpen, setDeactivatedDialogOpen] = useState(false);
  const [deactivatedUser, setDeactivatedUser] = useState(null);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [reactivateError, setReactivateError] = useState('');

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Ensure page is centered on load
  useEffect(() => {
    // Scroll to top to ensure proper centering
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    
    // Prevent any automatic scrolling and ensure content is centered
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    
    // Force layout recalculation
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50);

    return () => {
      clearTimeout(timer);
      // Clean up any style modifications
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  const formik = useFormik({
    initialValues: {
      userName: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const result = await login(values);
      if (result.success) {
        navigate(from, { replace: true });
      } else if (result.error && result.error.includes('deactivated')) {
        // Handle deactivated account
        setDeactivatedUser(values.userName);
        setDeactivatedDialogOpen(true);
      } else if (result.error && result.error.includes('deleted')) {
        // Handle deleted account
        sessionStorage.setItem('accountDeletedAccess', 'true'); // One-time access token
        navigate('/auth/account-deleted', { 
          state: { 
            message: 'Your account has been deleted. You can request account recovery below.',
            userName: values.userName,
            fromDeletedLogin: true // Security flag to allow access
          }
        });
      }
    },
  });

  const handleReactivateAccount = async () => {
    setReactivateLoading(true);
    setReactivateError('');
    try {
      // Use loginAndActivate endpoint which combines login and activation
      const response = await userService.loginAndActivate({
        userName: deactivatedUser,
        password: formik.values.password,
        activate: true
      });

      if (response.data.success) {
        // Store authentication data and update context
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Update auth context
        await login({userName: deactivatedUser,
        password: formik.values.password}, response.data.token);
        
        // Close dialog and navigate
        setDeactivatedDialogOpen(false);
        setDeactivatedUser(null);
        navigate(from, { replace: true });
      } else {
        setReactivateError(response.data.message || 'Failed to reactivate account');
      }
    } catch (err) {
      console.error('Reactivate account error:', err);
      const errorMessage = err.response?.data?.message || 'Network error. Please try again.';
      setReactivateError(errorMessage);
    } finally {
      setReactivateLoading(false);
    }
  };

  const handleCancelReactivation = () => {
    setDeactivatedDialogOpen(false);
    setDeactivatedUser(null);
    setReactivateError('');
    formik.resetForm();
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: 'auto', // Allow content to expand if needed
        display: 'flex',
        position: 'relative',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #0a0e27 0%, #1a237e 50%, #3949ab 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundAttachment: 'fixed', // Keep background fixed during scroll
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="7" cy="7" r="2"/%3E%3Ccircle cx="37" cy="7" r="2"/%3E%3Ccircle cx="7" cy="37" r="2"/%3E%3Ccircle cx="37" cy="37" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        },
        overflow: 'auto', // Allow scrolling if content is tall
      }}
    >
      <Container 
        component="main" 
        maxWidth="xl"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh', // Ensure minimum viewport height
          py: { xs: 2, sm: 3, lg: 4 }, // Responsive padding
          position: 'relative',
          zIndex: 1,
          px: { xs: 2, sm: 3, lg: 4 },
        }}
      >
        <Box
          data-testid="auth-container"
          sx={{
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr', 
              md: '1fr', 
              lg: '1.2fr 1fr',
              xl: '1.3fr 1fr'
            },
            gap: { xs: 0, lg: 6, xl: 8 },
            width: '100%',
            maxWidth: { xs: '500px', lg: '1200px', xl: '1400px' },
            alignItems: 'center',
            mx: 'auto',
          }}
        >
          {/* Left Side - Welcome Content */}
          <Fade in timeout={1000}>
            <Box
              sx={{
                display: { xs: 'none', lg: 'flex' },
                flexDirection: 'column',
                justifyContent: 'center',
                color: 'white',
                pr: { lg: 3, xl: 4 },
                minHeight: { lg: '600px' },
              }}
            >
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h2"
                  component="h1"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    mb: 2,
                    background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  IPL Fantasy League
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    opacity: 0.9,
                    fontWeight: 300,
                    lineHeight: 1.6,
                  }}
                >
                  Build your dream cricket team and compete with millions of fans worldwide
                </Typography>
              </Box>

              {/* Feature Cards */}
              <Box sx={{ space: 'y-4' }}>
                <Card
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    mb: 2,
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <TrendingUp sx={{ color: '#4fc3f7', mr: 2, fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Real-time Analytics
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Track your team performance with live stats
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    mb: 2,
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <Security sx={{ color: '#66bb6a', mr: 2, fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Secure Platform
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Your data is protected with enterprise-grade security
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <SportsCricket sx={{ color: '#ff7043', mr: 2, fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Expert Insights
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Get professional tips from cricket analysts
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Fade>

          {/* Right Side - Login Form */}
          <Slide direction="left" in timeout={800}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Paper
                elevation={24}
                sx={{
                  p: { xs: 3, sm: 4, lg: 5 },
                  borderRadius: 4,
                  backdropFilter: 'blur(20px)',
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)' 
                    : 'rgba(255, 255, 255, 0.95)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 20px 40px rgba(0, 0, 0, 0.4)'
                    : '0 20px 40px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease-in-out',
                  width: '100%',
                  maxWidth: { xs: '400px', sm: '450px', lg: '500px' },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 25px 50px rgba(0, 0, 0, 0.5)'
                      : '0 25px 50px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
              {/* Logo and Title */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    p: 2,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    boxShadow: '0 8px 24px rgba(25, 118, 210, 0.3)',
                  }}
                >
                  <SportsCricket sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography
                  component="h1"
                  variant="h4"
                  fontWeight="bold"
                  sx={{
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(45deg, #90caf9 30%, #1976d2 90%)'
                      : 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  Welcome Back
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ maxWidth: 300, mx: 'auto', lineHeight: 1.6 }}
                >
                  Sign in to your account to continue your fantasy cricket journey
                </Typography>
              </Box>

              {error && (
                <Slide direction="down" in={!!error}>
                  <Alert 
                    severity="error" 
                    sx={{ 
                      width: '100%', 
                      mb: 3,
                      borderRadius: 2,
                      '& .MuiAlert-icon': {
                        fontSize: '1.2rem',
                      },
                    }}
                  >
                    {error}
                  </Alert>
                </Slide>
              )}

              <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  id="userName"
                  name="userName"
                  label="Username"
                  type="text"
                  value={formik.values.userName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.userName && Boolean(formik.errors.userName)}
                  helperText={formik.touched.userName && formik.errors.userName}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      },
                      '&.Mui-focused': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                      },
                    },
                  }}
                />

                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  margin="normal"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                          sx={{
                            transition: 'transform 0.2s ease',
                            '&:hover': {
                              transform: 'scale(1.1)',
                            },
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      },
                      '&.Mui-focused': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                      },
                    },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 1,
                    mb: 2,
                    py: 1.8,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(25, 118, 210, 0.4)',
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                          },
                        }}
                      />
                      Signing In...
                    </Box>
                  ) : (
                    'Sign In'
                  )}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 3, mb: 3 }}>
                  <Link 
                    component={RouterLink} 
                    to="/auth/forgot-password" 
                    variant="body2"
                    sx={{ 
                      textDecoration: 'none',
                      color: theme.palette.mode === 'dark' ? '#90caf9' : 'primary.main',
                      fontWeight: 500,
                      transition: 'color 0.2s ease',
                      '&:hover': {
                        color: theme.palette.mode === 'dark' ? '#64b5f6' : 'primary.dark',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Forgot your password?
                  </Link>
                </Box>

                <Divider 
                  sx={{ 
                    my: 3,
                    '&::before, &::after': {
                      borderColor: 'divider',
                    },
                  }}
                >
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      px: 2,
                      backgroundColor: 'background.paper',
                      fontWeight: 500,
                    }}
                  >
                    OR
                  </Typography>
                </Divider>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Don't have an account?{' '}
                    <Link 
                      component={RouterLink} 
                      to="/auth/signup" 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'bold', 
                        textDecoration: 'none',
                        color: theme.palette.mode === 'dark' ? '#90caf9' : 'primary.main',
                        transition: 'color 0.2s ease',
                        '&:hover': {
                          color: theme.palette.mode === 'dark' ? '#64b5f6' : 'primary.dark',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Sign up here
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </Paper>
            </Box>
          </Slide>
        </Box>
      </Container>

      {/* Deactivated Account Dialog */}
      <Dialog 
        open={deactivatedDialogOpen} 
        onClose={handleCancelReactivation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonOff sx={{ color: 'warning.main', mr: 1 }} />
            Account Deactivated
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="600">
              Your account has been deactivated
            </Typography>
          </Alert>
          
          {reactivateError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {reactivateError}
            </Alert>
          )}
          
          <Typography variant="body1" gutterBottom>
            Hi <strong>{deactivatedUser}</strong>, your account is currently deactivated.
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Would you like to reactivate your account now? This will restore full access to your profile and all features.
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
            <Typography variant="body2">
              Your data and preferences will be restored
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
            <Typography variant="body2">
              You'll regain full access to all features
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCancelReactivation}
            disabled={reactivateLoading}
            color="inherit"
          >
            No, Stay Logged Out
          </Button>
          <Button 
            onClick={handleReactivateAccount}
            color="primary"
            variant="contained"
            disabled={reactivateLoading}
            startIcon={reactivateLoading ? <CircularProgress size={20} /> : <CheckCircle />}
            sx={{ borderRadius: 2 }}
          >
            {reactivateLoading ? 'Reactivating...' : 'Yes, Reactivate Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;